import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../../api/api';
import AuthContext from '../../contexts/AuthContext';
import { getAssignmentRange } from '../../utils/assignmentUtils';
import '../../styles/StudentAssignmentPage.css';

const StudentAssignmentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [versesText, setVersesText] = useState([]);
  const [isPracticed, setIsPracticed] = useState(false);
  const [practiceTimestamp, setPracticeTimestamp] = useState(null);
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const [audioUrls, setAudioUrls] = useState([]);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [currentReciter, setCurrentReciter] = useState('default');
  const [audioProgress, setAudioProgress] = useState(0);
  const audioRef = useRef(null);
  const [translations, setTranslations] = useState([]);
  
  // Quiz related states
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [quizVerses, setQuizVerses] = useState([]);
  const [quizTranslations, setQuizTranslations] = useState([]);
  const [selectedArabic, setSelectedArabic] = useState(null);
  const [selectedTranslation, setSelectedTranslation] = useState(null);
  const [quizMatches, setQuizMatches] = useState({});
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [quizAttempts, setQuizAttempts] = useState(0);
  const [showResults, setShowResults] = useState(false);
  
  // Check if a parent is viewing their child's assignment
  const isParentView = location.state?.fromParentChildView === true;
  const childId = location.state?.childId;
  const childName = location.state?.childName || 'Child';

  // Fetch assignment data
  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!id) {
          throw new Error('No assignment ID provided');
        }
        
        console.log('Fetching assignment details for ID:', id);
        
        // Make API call to get the specific assignment
        const response = await api.get(`/assignments/${id}`);
        const assignmentData = response.data.success 
          ? response.data.data 
          : response.data;
        
        console.log('Assignment data received:', assignmentData);
        setAssignment(assignmentData);

        // Fetch Quranic verses - supports both legacy and new range formats
        const range = getAssignmentRange(assignmentData);

        if (range && range.start && range.end) {
          // Check if single-surah assignment (audio player only supports single surah)
          if (range.start.surah.id === range.end.surah.id) {
            fetchQuranVerses(
              range.start.surah.id,
              range.start.verse,
              range.end.verse
            );
          } else {
            // Multi-surah assignments not supported for audio playback
            console.warn('Multi-surah assignments not supported in practice view audio player');
            setError('Multi-surah audio playback is not yet supported. Please practice each surah separately.');
          }
        } else {
          console.error('Assignment missing required surah/verse data:', assignmentData);
        }

        // Check practice status
        checkPracticeStatus(assignmentData._id);

      } catch (err) {
        console.error('Error fetching assignment details:', err);
        setError('Failed to load assignment details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAssignment();
  }, [id]);

  // Fetch Quran verses from external API
  const fetchQuranVerses = async (surahId, fromVerse, toVerse) => {
    try {
      const response = await fetch(`https://api.alquran.cloud/v1/surah/${surahId}`);
      const data = await response.json();
      
      if (data.code === 200 && data.data && data.data.ayahs) {
        // Filter verses based on the range
        const filteredVerses = data.data.ayahs
          .filter(ayah => 
            ayah.numberInSurah >= fromVerse && 
            ayah.numberInSurah <= toVerse
          )
          .map(ayah => ayah.text);
        
        setVersesText(filteredVerses);
        console.log(`Fetched ${filteredVerses.length} verses`);

        // Pre-generate audio URLs for each verse
        const urls = [];
        for (let i = 0; i < filteredVerses.length; i++) {
          const verseNumber = fromVerse + i;
          const url = await fetchQuranAudio(surahId, verseNumber, currentReciter);
          urls.push(url);
        }
        setAudioUrls(urls);
      }
    } catch (error) {
      console.error('Error fetching Quran verses:', error);
      setError('Failed to load Quran verses. Please try again later.');
    }
  };

  // Check if assignment has been practiced
  const checkPracticeStatus = async (assignmentId) => {
    try {
      if (!assignmentId) return;
      
      // Determine whose practice status we're checking - the logged-in user or a child
      const targetStudentId = isParentView ? childId : user?.id;
      
      if (!targetStudentId) {
        console.log('No student ID available to check practice status');
        return;
      }
      
      // Build query parameters
      let url = `/practice-logs?assignmentId=${encodeURIComponent(assignmentId)}`;
      url += `&studentId=${encodeURIComponent(targetStudentId)}`;
      
      console.log('Requesting practice logs:', url);
      const response = await api.get(url);
      
      console.log('Practice logs response:', response.data);
      
      // Check if we have any practice logs for this assignment
      if (response.data && response.data.length > 0) {
        const practiceLog = response.data[0]; // Get the most recent log
        setIsPracticed(true);
        setPracticeTimestamp(practiceLog.markedAt);
      } else {
        setIsPracticed(false);
        setPracticeTimestamp(null);
      }
    } catch (error) {
      console.error('Error checking practice logs:', error);
      // Silent fail for UI
    }
  };

  // Handle marking assignment as practiced
  const handleMarkAsPracticed = async () => {
    try {
      if (!assignment || !assignment._id) return;
      
      // Get the appropriate student ID - either the child ID when in parent view, or current user
      const targetStudentId = isParentView ? childId : user?.id;
      
      // Make sure we have a student ID
      if (!targetStudentId) {
        alert('Could not identify student for this assignment');
        return;
      }
      
      let response;
      
      // If already practiced, remove the practice log
      if (isPracticed) {
        response = await api.delete(`/practice-logs/assignment/${assignment._id}/practice`, {
          data: { studentId: targetStudentId }
        });
      } else {
        // Mark as practiced
        response = await api.post(`/practice-logs/assignment/${assignment._id}/practice`, {
          studentId: targetStudentId
        });
      }
      
      if (response.data.success) {
        // Toggle the practice status
        const newStatus = !isPracticed;
        setIsPracticed(newStatus);
        
        if (newStatus) {
          // If now practiced, set the timestamp
          setPracticeTimestamp(response.data.practiceLog?.markedAt);
        } else {
          // If practice removed, clear timestamp
          setPracticeTimestamp(null);
        }
        
        // Show confirmation
        alert(newStatus ? 'Assignment marked as practiced' : 'Practice status removed');
      }
    } catch (error) {
      console.error('Error updating practice status:', error);
      alert('Could not update practice status. ' + (error.response?.data?.message || error.message));
    }
  };

  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Format assignment title
  const formatAssignmentTitle = () => {
    if (!assignment) return 'Assignment';
    
    if (assignment.title) return assignment.title;
    
    if (assignment.surah && assignment.verses) {
      return `${assignment.surah.name} (${assignment.verses.from}-${assignment.verses.to})`;
    }
    
    return 'Assignment';
  };
  
  // Start the quiz
  const handleStartQuiz = () => {
    if (!assignment || !versesText || versesText.length === 0) {
      alert('Quiz content is not available');
      return;
    }

    // Quiz only supports new range format (all quiz-enabled assignments use range)
    if (!assignment.range || !assignment.range.start || !assignment.range.end) {
      alert('Quiz is not available for this assignment format');
      return;
    }

    const fromVerse = assignment.range.start.verse;
    const toVerse = assignment.range.end.verse;
    const surahId = assignment.range.start.surah.id;

    // Only support single-surah quizzes
    if (assignment.range.start.surah.id !== assignment.range.end.surah.id) {
      alert('Quiz is not available for multi-surah assignments');
      return;
    }

    // Determine how many verses to include (max 5)
    const totalVerses = toVerse - fromVerse + 1;
    const quizSize = Math.min(totalVerses, 5);

    // If more than 5 verses, select random verses
    let selectedIndices = [];
    if (totalVerses <= 5) {
      // Use all verses if 5 or fewer
      selectedIndices = Array.from({length: totalVerses}, (_, i) => i);
    } else {
      // Randomly select 5 verses
      while (selectedIndices.length < quizSize) {
        const randomIndex = Math.floor(Math.random() * totalVerses);
        if (!selectedIndices.includes(randomIndex)) {
          selectedIndices.push(randomIndex);
        }
      }
    }

    // Fetch translations if not already available
    const fetchTranslations = async () => {
      if (!translations || translations.length === 0) {
        try {
          const response = await fetch(`https://api.alquran.cloud/v1/surah/${surahId}/en.sahih`);
          const data = await response.json();

          if (data.code === 200 && data.data && data.data.ayahs) {
            const fetchedTranslations = data.data.ayahs
              .filter(ayah =>
                ayah.numberInSurah >= fromVerse &&
                ayah.numberInSurah <= toVerse
              )
              .map(ayah => ayah.text);

            setTranslations(fetchedTranslations);
            return fetchedTranslations;
          }
          return [];
        } catch (error) {
          console.error('Error fetching translations:', error);
          return [];
        }
      }
      return translations;
    };

    // Prepare and start the quiz
    const startQuiz = async () => {
      const translationTexts = await fetchTranslations();

      // Prepare quiz data
      const verses = selectedIndices.map(index => {
        const ayahNumber = fromVerse + index;
        return {
          id: `verse-${ayahNumber}`,
          ayahNumber,
          arabic: versesText[index] || `Verse ${ayahNumber}`,
          matched: false,
          correctMatchId: `translation-${ayahNumber}`
        };
      });

      const translations = selectedIndices.map(index => {
        const ayahNumber = fromVerse + index;
        const text = translationTexts[index] || `Translation ${ayahNumber}`;
        return {
          id: `translation-${ayahNumber}`,
          ayahNumber,
          text: text,
          matched: false,
          correctMatchId: `verse-${ayahNumber}`
        };
      });
      
      // Shuffle the arrays
      const shuffledVerses = [...verses].sort(() => Math.random() - 0.5);
      const shuffledTranslations = [...translations].sort(() => Math.random() - 0.5);
      
      setQuizVerses(shuffledVerses);
      setQuizTranslations(shuffledTranslations);
      setSelectedArabic(null);
      setSelectedTranslation(null);
      setQuizMatches({});
      setQuizCompleted(false);
      setQuizScore(0);
      setShowResults(false);
      setQuizAttempts(prev => prev + 1);
      setShowQuizModal(true);
    };
    
    startQuiz();
  };

  // Handle selection of Arabic verse
  const selectArabic = (index) => {
    if (quizVerses[index].matched) return;
    
    setSelectedArabic(index);
    
    // If a translation is already selected, check for a match
    if (selectedTranslation !== null) {
      checkForMatch(index, selectedTranslation);
    }
  };

  // Handle selection of translation
  const selectTranslation = (index) => {
    if (quizTranslations[index].matched) return;
    
    setSelectedTranslation(index);
    
    // If an Arabic verse is already selected, check for a match
    if (selectedArabic !== null) {
      checkForMatch(selectedArabic, index);
    }
  };

  // Check if the selected items match
  const checkForMatch = (arabicIndex, translationIndex) => {
    const verse = quizVerses[arabicIndex];
    const translation = quizTranslations[translationIndex];
    
    // Check if correct match
    const isCorrect = verse.correctMatchId === translation.id;
    
    // Update the matches state
    const newMatches = {...quizMatches};
    newMatches[verse.id] = {
      translationId: translation.id,
      isCorrect
    };
    
    setQuizMatches(newMatches);
    
    // Mark as matched
    const newVerses = [...quizVerses];
    const newTranslations = [...quizTranslations];
    
    newVerses[arabicIndex] = {...verse, matched: true};
    newTranslations[translationIndex] = {...translation, matched: true};
    
    setQuizVerses(newVerses);
    setQuizTranslations(newTranslations);
    
    // Clear selections
    setSelectedArabic(null);
    setSelectedTranslation(null);
    
    // Check if all items are matched
    const allMatched = newVerses.every(v => v.matched);
    if (allMatched) {
      // Calculate score
      const correctMatches = Object.values(newMatches).filter(m => m.isCorrect).length;
      const score = Math.round((correctMatches / quizVerses.length) * 100);
      setQuizScore(score);
      setQuizCompleted(true);
      
      // Submit quiz results to backend if needed
      submitQuizResults(quizVerses.length, correctMatches);
    }
  };

  // Submit quiz results to the backend
  const submitQuizResults = async (totalQuestions, correctAnswers) => {
    try {
      // Store results in local state first
      const quizResult = {
        assignmentId: assignment._id,
        totalQuestions,
        correctAnswers,
        score: Math.round((correctAnswers / totalQuestions) * 100),
        startedAt: new Date(),
        completedAt: new Date(),
        completed: true
      };
      
      // Submit to backend if online
      try {
        const response = await api.post('/quizzes/attempt', quizResult);
        console.log('Quiz results submitted:', response.data);
      } catch (error) {
        console.error('Error submitting quiz results:', error);
      }
      
      // Show results after submission
      setShowResults(true);
    } catch (error) {
      console.error('Error in quiz submission:', error);
    }
  };

  // Reset quiz for another attempt
  const resetQuiz = () => {
    handleStartQuiz();
  };

  // Fetch audio URL for a verse
  const fetchQuranAudio = async (surahId, verseNumber, reciterOption = 'default') => {
    try {
      // Format the surah and verse numbers
      const surahFormatted = surahId.toString().padStart(3, '0');  
      const verseFormatted = verseNumber.toString().padStart(3, '0');
      
      // Define reciter options
      const reciters = {
        default: 'Alafasy_128kbps', // Mishary Rashid Alafasy
        husary: 'Husary_128kbps',   // Mahmoud Khalil Al-Husary
        minshawi: 'Minshawy_Murattal_128kbps' // Mohamed Siddiq El-Minshawi
      };
      
      // Get the selected reciter path
      const reciterPath = reciters[reciterOption] || reciters.default;
      
      // Construct URL
      const audioUrl = `https://everyayah.com/data/${reciterPath}/${surahFormatted}${verseFormatted}.mp3`;
      
      return audioUrl;
    } catch (error) {
      console.error('Error fetching Quran audio:', error);
      return null;
    }
  };

  // Handle audio playback
  const toggleAudio = () => {
    if (!audioRef.current) return;
    
    if (audioPlaying) {
      audioRef.current.pause();
      setAudioPlaying(false);
    } else {
      audioRef.current.play();
      setAudioPlaying(true);
    }
  };

  // Navigate to next verse
  const goToNextVerse = () => {
    if (currentAudioIndex < audioUrls.length - 1) {
      setCurrentAudioIndex(currentAudioIndex + 1);
    }
  };

  // Navigate to previous verse
  const goToPreviousVerse = () => {
    if (currentAudioIndex > 0) {
      setCurrentAudioIndex(currentAudioIndex - 1);
    }
  };

  // Update audio progress
  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    
    const progress = audioRef.current.currentTime / audioRef.current.duration;
    setAudioProgress(isNaN(progress) ? 0 : progress);
  };

  // Handle audio end
  const handleAudioEnded = () => {
    setAudioPlaying(false);
    
    // Auto-advance to next verse if available
    if (currentAudioIndex < audioUrls.length - 1) {
      setCurrentAudioIndex(currentAudioIndex + 1);
      
      // Auto-play next verse after a short delay
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play();
          setAudioPlaying(true);
        }
      }, 500);
    }
  };

  // Update audio source when index changes
  useEffect(() => {
    if (audioRef.current && audioUrls[currentAudioIndex]) {
      audioRef.current.src = audioUrls[currentAudioIndex];
      audioRef.current.load();
    }
  }, [currentAudioIndex, audioUrls]);

  // Change reciter
  const handleReciterChange = (e) => {
    const newReciter = e.target.value;
    setCurrentReciter(newReciter);

    // Reset audio state
    setAudioPlaying(false);

    // Regenerate audio URLs with new reciter (supports both legacy and range formats)
    if (assignment) {
      const range = getAssignmentRange(assignment);

      if (range && range.start && range.end) {
        // Only single-surah supported for audio
        if (range.start.surah.id === range.end.surah.id) {
          regenerateAudioUrls(
            range.start.surah.id,
            range.start.verse,
            range.end.verse,
            newReciter
          );
        }
      }
    }
  };

  // Regenerate audio URLs with new reciter
  const regenerateAudioUrls = async (surahId, fromVerse, toVerse, reciter) => {
    try {
      const urls = [];
      for (let i = 0; i < (toVerse - fromVerse + 1); i++) {
        const verseNumber = fromVerse + i;
        const url = await fetchQuranAudio(surahId, verseNumber, reciter);
        urls.push(url);
      }
      setAudioUrls(urls);
      setCurrentAudioIndex(0);
    } catch (error) {
      console.error('Error regenerating audio URLs:', error);
    }
  };

  // Handle audio seek
  const handleSeek = (e) => {
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const percentage = offsetX / rect.width;
    
    if (audioRef.current && !isNaN(audioRef.current.duration)) {
      audioRef.current.currentTime = percentage * audioRef.current.duration;
    }
  };

  if (loading) {
    return (
      <div className="student-assignment-loading">
        <div className="loading-spinner"></div>
        <p>Loading assignment...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-assignment-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button 
          className="retry-button"
          onClick={() => navigate('/dashboard')}
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="student-assignment-error">
        <h2>Assignment Not Found</h2>
        <p>The requested assignment could not be found.</p>
        <button 
          className="retry-button"
          onClick={() => navigate('/dashboard')}
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="student-assignment-container">
      {/* Header */}
      <div className="assignment-header">
        {isParentView ? (
          <Link to={`/assignments/student`} className="back-link" state={location.state}>
            &larr; Back to {childName}'s Assignments
          </Link>
        ) : (
          <Link to="/assignments/student" className="back-link">
            &larr; Back to My Assignments
          </Link>
        )}
        <h1>Assignment Details</h1>
      </div>
      
      {/* Assignment Info Block */}
      <div className="assignment-info-block">
        <div className="assignment-title-block">
          <h2>{formatAssignmentTitle()}</h2>
          <div className="assignment-due-date">
            Due: {formatDate(assignment.dueDate)}
          </div>
          {(() => {
            const range = getAssignmentRange(assignment);
            if (range && range.start && range.end) {
              const fromVerse = range.start.verse;
              const toVerse = range.end.verse;
              const isSingleSurah = range.start.surah.id === range.end.surah.id;

              return (
                <div className="assignment-verses">
                  {isSingleSurah ? (
                    <>Verses {fromVerse}-{toVerse}</>
                  ) : (
                    <>
                      Multi-surah: {range.start.surah.name} ({fromVerse}) to {range.end.surah.name} ({toVerse})
                    </>
                  )}
                </div>
              );
            }
            return null;
          })()}
        </div>
      </div>
      
      {/* Arabic Text */}
      <div className="arabic-text-card">
        <div className="arabic-text-container">
          {versesText.map((verse, index) => (
            <div 
              key={index} 
              className={`verse ${currentAudioIndex === index ? 'active-verse' : ''}`}
            >
              <p className="arabic-text">{verse}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Reciter Selection */}
      <div className="reciter-selection">
        <label htmlFor="reciter">Choose Reciter:</label>
        <select 
          id="reciter" 
          value={currentReciter} 
          onChange={handleReciterChange}
          className="reciter-dropdown"
        >
          <option value="default">Mishary Rashid Alafasy</option>
          <option value="husary">Mahmoud Khalil Al-Husary</option>
          <option value="minshawi">Mohamed Siddiq El-Minshawi</option>
        </select>
      </div>

      {/* Audio Player */}
      <div className="audio-player">
        <audio 
          ref={audioRef}
          src={audioUrls[currentAudioIndex]}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleAudioEnded}
        />
        
        <div className="audio-controls">
          <button 
            className="audio-button prev-button" 
            onClick={goToPreviousVerse}
            disabled={currentAudioIndex === 0}
          >
            ◀
          </button>
          
          <button 
            className="audio-button play-button" 
            onClick={toggleAudio}
          >
            {audioPlaying ? '⏸' : '▶'}
          </button>
          
          <button 
            className="audio-button next-button" 
            onClick={goToNextVerse}
            disabled={currentAudioIndex >= audioUrls.length - 1}
          >
            ▶
          </button>
        </div>
        
        <div className="progress-container" onClick={handleSeek}>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${audioProgress * 100}%` }}
            />
          </div>
        </div>
        
        <div className="verse-indicator">
          Verse {currentAudioIndex + 1} of {audioUrls.length}
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="action-buttons">
        {(assignment.matchingQuiz || assignment.assessment?.matchingQuiz) && (
          <button 
            className="quiz-button"
            onClick={handleStartQuiz}
          >
            Play Ayats Meaning Quiz
          </button>
        )}
        
        <button 
          className={`practice-button ${isPracticed ? 'practiced' : ''}`}
          onClick={handleMarkAsPracticed}
        >
          {isPracticed ? 'Practiced ✓' : 'Mark as Practiced'}
        </button>
        
        {isPracticed && practiceTimestamp && (
          <div className="practice-timestamp">
            Practiced on: {formatDate(practiceTimestamp)}
          </div>
        )}
      </div>
      
      {/* Quiz Modal */}
      {showQuizModal && (
        <div className="quiz-modal-overlay" onClick={() => {
          if (!quizCompleted) {
            if (window.confirm("Quit Quiz? Your progress will be lost.")) {
              setShowQuizModal(false);
            }
          } else {
            setShowQuizModal(false);
          }
        }}>
          <div className="quiz-modal" onClick={(e) => e.stopPropagation()}>
            {/* Quiz Header */}
            <div className="quiz-header">
              <h2 className="quiz-title">Ayats Meaning Quiz</h2>
              <button className="quiz-close-button" onClick={() => {
                if (!quizCompleted) {
                  if (window.confirm("Quit Quiz? Your progress will be lost.")) {
                    setShowQuizModal(false);
                  }
                } else {
                  setShowQuizModal(false);
                }
              }}>×</button>
            </div>
            
            {showResults ? (
              /* Results View */
              <div className="quiz-results-container">
                <div className="quiz-score-container">
                  <p className="quiz-score-text">Your Score</p>
                  <p className="quiz-score-value">{quizScore}%</p>
                  <p className="quiz-score-detail">
                    {Object.values(quizMatches).filter(m => m.isCorrect).length} correct out of {quizVerses.length}
                  </p>
                </div>
                
                <h3 className="correct-matches-title">Correct Translations</h3>
                
                {/* Show all verses with their correct translations */}
                <div className="result-items-container">
                  {quizVerses.map((verse, index) => {
                    const matchingTranslation = quizTranslations.find(t => t.correctMatchId === verse.id);
                    return (
                      <div key={`result-${verse.ayahNumber}`} className="result-item">
                        <p className="result-verse">Verse {verse.ayahNumber}</p>
                        <p className="result-arabic">{verse.arabic}</p>
                        <p className="result-translation">{matchingTranslation?.text || 'Translation not available'}</p>
                      </div>
                    );
                  })}
                </div>
                
                <div className="quiz-buttons-container">
                  <button 
                    className="try-again-button"
                    onClick={resetQuiz}
                  >
                    Try Again
                  </button>
                  
                  <button 
                    className="close-quiz-button"
                    onClick={() => setShowQuizModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              /* Quiz View */
              <>
                <p className="quiz-instructions">
                  Match each verse with its correct meaning by clicking one from each column.
                </p>
                
                <div className="matching-columns">
                  {/* Translations Column */}
                  <div className="quiz-column">
                    <h3 className="column-header">Translations</h3>
                    <div className="column-scroll">
                      {quizTranslations.map((translation, index) => (
                        <div
                          key={translation.id}
                          className={`quiz-item translation-item 
                            ${selectedTranslation === index ? 'selected-quiz-item' : ''} 
                            ${translation.matched ? 'matched-quiz-item' : ''}
                            ${Object.values(quizMatches).find(m => 
                              m.translationId === translation.id && !m.isCorrect
                            ) ? 'incorrect-match-quiz-item' : ''}`}
                          onClick={() => !translation.matched && selectTranslation(index)}
                        >
                          <p className={`quiz-translation-text 
                            ${translation.matched ? 'matched-quiz-item-text' : ''}`}>
                            {translation.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Arabic Verses Column */}
                  <div className="quiz-column">
                    <h3 className="column-header">Arabic Verses</h3>
                    <div className="column-scroll">
                      {quizVerses.map((verse, index) => (
                        <div
                          key={verse.id}
                          className={`quiz-item arabic-item 
                            ${selectedArabic === index ? 'selected-quiz-item' : ''} 
                            ${verse.matched ? 'matched-quiz-item' : ''}
                            ${quizMatches[verse.id] && !quizMatches[verse.id].isCorrect ? 'incorrect-match-quiz-item' : ''}`}
                          onClick={() => !verse.matched && selectArabic(index)}
                        >
                          <p className={`quiz-arabic-text 
                            ${verse.matched ? 'matched-quiz-item-text' : ''}`}>
                            {verse.arabic}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {quizCompleted && (
                  <button 
                    className="show-results-button"
                    onClick={() => setShowResults(true)}
                  >
                    Show Results
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentAssignmentPage;